/* ═══════════════════════════════════════════════════════════════
   Forma paper — the INTERACTIVE band (the one way zones are edited).

   Every token on the sheet is a selectable, draggable, droppable object
   (CanvasSelectionContext, DESIGN.md §4's copy/cut/paste triad): click,
   Ctrl/Shift-click, drag onto a slot or another band, right-click for the
   format menu, Ctrl+Left/Right to reorder, × to remove. Line breaks and the
   {Line} rule are objects too. There is no Header & Footer panel anywhere —
   Forma Studio and the project editor both work zones here.

   Layout is the display band's own hook (ZoneBand.tsx), so the editable
   band and the printed one can never disagree about where a token sits.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { StandardConfig } from '../../../shared/types';
import { isNumberedPlaceholder } from '../../../shared/forma-placeholders';
import { tokBase } from '../../utils';
import { ALL_TOKENS } from '../../constants';
import {
  CanvasSelectionContext,
  TokenClipboardContext,
  TokenFormatMenu,
  TokenSlotMenu,
  slotClipboardItems,
  type TokenMenuItem,
} from '../TokenComponents';
import {
  COLUMN_LABEL,
  zoneMoveAt,
  zoneNudgeBlockReason,
  zoneNudgeTargetIdx,
  zoneRemoveAt,
  zoneUpdateAt,
} from '../formaZonesModel';
import { applySlotDrop, useZoneSlotDrag, type SlotColumn } from './useZoneSlotDrag';
import { SlotDropLayer } from './SlotDropLayer';
import { beginZoneDrag, measureTokenKeys } from './zoneDragSession';
import type { TextDialogRequest } from '../panels/FormaPalettes';
import './paper.css';
import {
  renderBandRows,
  useZoneBandLayout,
  type ZoneBandProps,
  type ZoneColumnKey,
} from './ZoneBand';

export interface ZoneBandEditProps extends ZoneBandProps {
  /** Commits the on-sheet context
   *  menu's Delete/Edit-text/format actions as ordinary undoable forma
   *  edits (contract §2.7). Omitted → Delete/Edit are unavailable (a
   *  read-only or non-edit caller never reaches here anyway). */
  edit?: (fn: (f: StandardConfig) => StandardConfig) => void;
  /** Opens the shared literal-text editor (TextTokenDialog) — used by the
   *  on-sheet context menu's "Edit text" action and by clicking an empty
   *  band to add the first line of text (Josh, 2026-08-13, Order 1). */
  onRequestTextEdit?: (req: TextDialogRequest) => void;
  /** Drop a token at an EXACT position in a zone (before/after another token,
   *  optionally starting a new line) — the classic canvas's insertAtInZone,
   *  restored on the sheet. Omitted → a drop onto a token does nothing and
   *  the band's append-only drop (ZoneShell) is the only path, exactly as
   *  every non-Studio caller behaves today. */
  onZoneInsertAt?: (
    zoneKey: string,
    idx: number,
    value: string,
    srcZone: string | null,
    srcIdx: number | null,
    newLine: boolean
  ) => void;
  /** Drop a token ONTO another to replace it — the classic replaceAtInZone. */
  onZoneReplaceAt?: (
    zoneKey: string,
    idx: number,
    value: string,
    srcZone: string | null,
    srcIdx: number | null
  ) => void;
  /** The Studio's status whisper — says why a blocked nudge did nothing
   *  ("Already first in the zone"). Omitted → the token pulses only. */
  onNotice?: (message: string) => void;
}

function isCustomTokenValue(tok: string): boolean {
  if (tok === '\n') return false;
  const base = tokBase(tok);
  if (base.startsWith('{Image:')) return false;
  if (base === '{EndOfSection}') return false;
  if (base.startsWith('{ProjectToken:')) return false;
  if (isNumberedPlaceholder(base)) return false;
  if (ALL_TOKENS.find((x) => x.value === base)) return false;
  return true;
}

export function ZoneBandEdit(props: ZoneBandEditProps) {
  const { kind, edit, onRequestTextEdit, onZoneInsertAt, onZoneReplaceAt, onNotice } = props;
  const canvasSel = React.useContext(CanvasSelectionContext);
  const clip = React.useContext(TokenClipboardContext);
  // Slot targeting for drags over this band's three columns (item 1): the
  // hook reads the columns lazily through this ref (zones are derived below).
  const bandRef = React.useRef<HTMLDivElement>(null);
  const instanceId = React.useId();
  const slotCols = React.useRef<SlotColumn[]>([]);
  const canDropOnToken = !!(onZoneInsertAt || onZoneReplaceAt);
  const slot = useZoneSlotDrag({
    rootRef: bandRef,
    enabled: canDropOnToken,
    columnAt: (x, rr) => {
      const t = (x - rr.left) / Math.max(1, rr.width);
      return slotCols.current[t < 1 / 3 ? 0 : t < 2 / 3 ? 1 : 2] ?? null;
    },
    columns: () => slotCols.current,
    onDropAt: (target, e) =>
      applySlotDrop(target, e, {
        insertAt: (z, idx, value, srcZ, srcI, newLine) =>
          onZoneInsertAt?.(z, idx, value, srcZ, srcI, newLine),
        replaceAt: (z, idx, value, srcZ, srcI) => onZoneReplaceAt?.(z, idx, value, srcZ, srcI),
        moveTokensAt: (z, idx, keys, copy, newLine) =>
          canvasSel.moveTokensAt(z, idx, keys, copy, newLine),
      }),
  });
  /** Right-click menu for an L/C/R column region, or for a line-break marker. */
  const [slotMenu, setSlotMenu] = React.useState<{
    x: number;
    y: number;
    label: string;
    items: TokenMenuItem[];
  } | null>(null);
  // Anchors for the on-sheet context menu (TokenFormatMenu positions off the
  // real DOM node) — keyed by `token:<zoneKey>:<idx>`, refreshed every render.
  const spanRefs = React.useRef<Record<string, HTMLElement | null>>({});
  const { zones, rows, rowsSrc, resolveToken, lineTok, rule, rowSpacing, ruleBelow } =
    useZoneBandLayout(props);

  // ─── On-sheet direct manipulation (Josh, 2026-08-13): a token rendered on
  // the paper is the SAME selectable/draggable object the panel's ZoneChip
  // is — same `token:<zoneKey>:<idx>` key, same CanvasSelectionContext, so
  // copy/cut/paste/Delete (DESIGN.md §4) and cross-band drag work the
  // moment a token is wired in. `layoutZonesIndexed` (the same wrap/pack/zip
  // brain as `layoutZones`, additive) hands back each rendered fragment's
  // ORIGIN INDEX directly — no more matching by value, so a literal that
  // word-wraps across several visual lines stays addressable as its ONE
  // source token on every fragment (the gap the classic value-matching hack
  // couldn't close). That index is local to the `{Line}`-filtered array fed
  // to the layout brain; `rawMap` below translates it back to the position
  // in the REAL zone array (`readZone`'s indexing) that a `{Line}` rule
  // token — filtered out of layout, never gone from storage — would
  // otherwise throw off.
  const zoneKeyOf = (col: 'L' | 'C' | 'R') =>
    col === 'L' ? zones.keyL : col === 'C' ? zones.keyC : zones.keyR;
  slotCols.current = [
    { zoneKey: zones.keyL, align: 'left', label: 'left' },
    { zoneKey: zones.keyC, align: 'center', label: 'center' },
    { zoneKey: zones.keyR, align: 'right', label: 'right' },
  ];
  const rawArrOf = (col: 'L' | 'C' | 'R'): string[] =>
    (col === 'L' ? zones.L : col === 'C' ? zones.C : zones.R) || [];
  const rawIndexMap = (col: 'L' | 'C' | 'R'): number[] => {
    const map: number[] = [];
    rawArrOf(col).forEach((v, i) => {
      if (!v.startsWith('{Line')) map.push(i);
    });
    return map;
  };
  const rawMapL = rawIndexMap('L');
  const rawMapC = rawIndexMap('C');
  const rawMapR = rawIndexMap('R');
  const rawMapOf = (col: 'L' | 'C' | 'R') =>
    col === 'L' ? rawMapL : col === 'C' ? rawMapC : rawMapR;

  // Each visual row's origin indices, translated back into RAW zone-array
  // space. A literal that WRAPS occupies several rows carrying the SAME
  // origin index — which is exactly why a line-break marker can't be derived
  // per fragment; it has to be derived from the gap BETWEEN two rows' raw
  // indices (see breaksAroundRow).
  const rowRawIdxs = (col: ZoneColumnKey): number[][] => {
    const rawMap = rawMapOf(col);
    return rowsSrc.map((s) =>
      s[col]
        .map((v) => (v != null ? (rawMap[v] ?? null) : null))
        .filter((v): v is number => v != null)
    );
  };
  const rowRawL = rowRawIdxs('L');
  const rowRawC = rowRawIdxs('C');
  const rowRawR = rowRawIdxs('R');
  const rowRawOf = (col: ZoneColumnKey) =>
    col === 'L' ? rowRawL : col === 'C' ? rowRawC : rowRawR;

  /** The raw `'\n'` indices that CLOSE a column's visual row (rendered as a ↵
   *  at the end of it) and — for the column's first row with content — the
   *  ones that OPEN it. A `'\n'` is consumed by the layout brain as a line
   *  splitter and never reaches a row, so without this it is invisible on the
   *  paper AND impossible to delete: exactly the functionality the classic
   *  canvas's DropZone had and the sheet lost. */
  const breaksAroundRow = (col: ZoneColumnKey, ri: number): { open: number[]; close: number[] } => {
    const raw = rawArrOf(col);
    const perRow = rowRawOf(col);
    const cur = perRow[ri] || [];
    if (!cur.length) return { open: [], close: [] };
    const lastSrc = Math.max(...cur);
    let nextFirst = raw.length;
    for (let k = ri + 1; k < perRow.length; k++) {
      if (perRow[k].length) {
        nextFirst = Math.min(...perRow[k]);
        break;
      }
    }
    const close: number[] = [];
    for (let i = lastSrc + 1; i < nextFirst && i < raw.length; i++) {
      if (raw[i] === '\n') close.push(i);
    }
    const open: number[] = [];
    if (perRow.findIndex((r) => r.length > 0) === ri) {
      const firstSrc = Math.min(...cur);
      for (let i = 0; i < firstSrc; i++) if (raw[i] === '\n') open.push(i);
    }
    return { open, close };
  };

  const openTokenEdit = (zoneKey: string, idx: number, raw: string) => {
    if (!onRequestTextEdit || !edit) return;
    if (raw.startsWith('{') && raw.endsWith('}')) return; // fields aren't literal text
    const pipe = raw.lastIndexOf('|');
    const base = pipe > 0 ? raw.slice(0, pipe) : raw;
    const suffix = pipe > 0 ? raw.slice(pipe) : '';
    onRequestTextEdit({
      title: 'Edit text',
      initial: base,
      onCommit: (text) => edit((f) => zoneUpdateAt(f, zoneKey, idx, text + suffix)),
    });
  };

  /** One nudge of a token: moves it (crossing line breaks) and re-selects it at
   *  its new index so repeated presses keep moving the same token. A blocked
   *  nudge pulses the token and whispers why. Returns the new index or false. */
  const nudgeToken = (
    zoneKey: string,
    arr: string[],
    idx: number,
    dir: -1 | 1,
    key: string
  ): number | false => {
    const reason = zoneNudgeBlockReason(arr, idx, dir);
    if (reason || !edit) {
      document.querySelectorAll(`[data-token-key="${CSS.escape(key)}"]`).forEach((el) => {
        el.classList.remove('dsg-nudge-blocked');
        void (el as HTMLElement).offsetWidth; // restart the pulse on a repeat press
        el.classList.add('dsg-nudge-blocked');
      });
      if (reason) onNotice?.(reason);
      return false;
    }
    const to = zoneNudgeTargetIdx(arr, idx, dir) as number;
    edit((f) => zoneMoveAt(f, zoneKey, idx, dir));
    canvasSel.select(`token:${zoneKey}:${to}`);
    return to;
  };

  /** A line-break marker rendered as a real, selectable object on the paper. */
  const breakMarker = (col: ZoneColumnKey, rawIdx: number) => {
    const zoneKey = zoneKeyOf(col);
    const key = `token:${zoneKey}:${rawIdx}`;
    const selected = canvasSel.selectedTokens.includes(key);
    return (
      <span
        key={`br${rawIdx}`}
        data-token-key={key}
        data-slot-trail-zone={rawIdx === rawArrOf(col).length - 1 ? zoneKey : undefined}
        data-slot-trail-raw={rawIdx === rawArrOf(col).length - 1 ? rawIdx : undefined}
        role="button"
        tabIndex={0}
        title="Line break"
        aria-label="Line break — select and press Delete, or right-click to remove"
        // Selection is announced, not just tinted — colour is never the only
        // signal for state (DESIGN.md §7).
        aria-pressed={selected}
        className={`dsg-token-break${selected ? ' is-selected' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          canvasSel.select(key, {
            additive: e.ctrlKey || e.metaKey,
            shift: e.shiftKey,
          });
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          canvasSel.select(key);
          setSlotMenu({
            x: e.clientX,
            y: e.clientY,
            label: 'Line break actions',
            items: [
              {
                label: 'Delete line break',
                enabled: !!edit,
                danger: true,
                run: () => {
                  edit?.((f) => zoneRemoveAt(f, zoneKey, rawIdx));
                  canvasSel.select(null);
                },
              },
              ...(clip ? slotClipboardItems(clip, zoneKey, rawArrOf(col).length > 0) : []),
            ],
          });
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            canvasSel.select(key);
          }
        }}
      >
        ↵
        {/* The same inline × every token carries (Josh, 2026-08-17: "the
            return doesn't have an x either") — one removal language. */}
        {edit && (
          <button
            type="button"
            className="dsg-token-x"
            title="Remove line break"
            aria-label="Remove line break"
            draggable={false}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              edit((f) => zoneRemoveAt(f, zoneKey, rawIdx));
              canvasSel.select(null);
            }}
          >
            <X size={10} strokeWidth={2.2} aria-hidden />
          </button>
        )}
      </span>
    );
  };

  const renderZone = (
    tokens: string[],
    col: ZoneColumnKey,
    origIdxs: (number | null)[],
    rowIdx: number
  ) => {
    const nodes: React.ReactNode[] = [];
    const zoneKey = zoneKeyOf(col);
    const rawArr = rawArrOf(col);
    const rawMap = rawMapOf(col);
    const breaks = breaksAroundRow(col, rowIdx);
    breaks.open.forEach((nl) => nodes.push(breakMarker(col, nl)));
    tokens.forEach((t, i) => {
      const node = resolveToken(t, i);
      if (node == null) return;
      const prev = i > 0 ? tokens[i - 1] : null;
      const needSpace =
        nodes.length > 0 &&
        prev !== '\n' &&
        tokBase(t) !== '{Space}' &&
        prev &&
        tokBase(prev) !== '{Space}';
      const filteredIdx = origIdxs[i] ?? null;
      const origIdx = filteredIdx != null ? (rawMap[filteredIdx] ?? null) : null;
      const myKey = origIdx != null ? `token:${zoneKey}:${origIdx}` : null;
      const rawValue = origIdx != null ? (rawArr[origIdx] ?? t) : t;
      const selected = !!myKey && canvasSel.selectedTokens.includes(myKey);
      const menuOpen =
        !!myKey &&
        canvasSel.menuKey === myKey &&
        (canvasSel.menuOwner == null || canvasSel.menuOwner === instanceId);
      // A literal that word-wraps paints as SEVERAL fragments (one per visual
      // row), all under this ONE origin index. They must read as ONE object
      // (Josh, 2026-08-13: "one token looks like 3 different tokens"): the
      // fragment position drives continuous styling (rounded only at the
      // ends), hover on any fragment lights every kin fragment, and exactly
      // one — the last — carries the inline × remove.
      const perRow = rowRawOf(col);
      const fragRows: number[] = [];
      if (origIdx != null)
        perRow.forEach((r, k) => {
          if (r.includes(origIdx)) fragRows.push(k);
        });
      const fragPos =
        fragRows.length <= 1
          ? 'only'
          : rowIdx === fragRows[0]
            ? 'first'
            : rowIdx === fragRows[fragRows.length - 1]
              ? 'last'
              : 'mid';
      const kinHover = (on: boolean) => {
        if (!myKey) return;
        document
          .querySelectorAll(`[data-token-key="${myKey}"]`)
          .forEach((el) => el.classList.toggle('is-kin-hover', on));
      };
      const wrapped = myKey ? (
        <span
          key={i}
          ref={(el) => {
            spanRefs.current[myKey] = el;
          }}
          data-token-key={myKey}
          data-slot-token="1"
          data-slot-zone={zoneKey}
          data-slot-line={rowIdx}
          data-raw-idx={origIdx as number}
          data-frag={fragPos}
          draggable
          role="button"
          tabIndex={0}
          aria-pressed={selected}
          aria-label={`${tokBase(t)} — click to select, drag to move, drag to a gap to place it, right-click to format, Control plus Left or Right arrow to reorder`}
          className={['dsg-ptoken', `is-frag-${fragPos}`, selected ? 'is-selected' : '']
            .filter(Boolean)
            .join(' ')}
          onMouseEnter={() => kinHover(true)}
          onMouseLeave={() => kinHover(false)}
          onDragStart={(e) => {
            const copy = e.ctrlKey || e.metaKey;
            // A multi-token selection (marquee or Ctrl/Shift-click) drags as ONE
            // unit — the same rule the panel's ZoneChip has always used. Without
            // this the sheet only ever moved the single token under the cursor
            // and silently left the rest of the selection behind.
            const isMulti = selected && canvasSel.selectedTokens.length > 1;
            if (isMulti) {
              e.dataTransfer.setData(
                'application/json',
                JSON.stringify({ type: 'tokenMulti', keys: canvasSel.selectedTokens, copy })
              );
            } else {
              e.dataTransfer.setData(
                'application/json',
                JSON.stringify({
                  type: 'token',
                  value: rawValue,
                  sourceZone: zoneKey,
                  sourceIndex: origIdx,
                  copy,
                })
              );
            }
            e.dataTransfer.effectAllowed = copy ? 'copy' : 'copyMove';
            // Tell the zone's slot targeting how wide the gap must open, and
            // dim the old place(s) while the token is in the air.
            const dragKeys = isMulti ? canvasSel.selectedTokens : [myKey];
            beginZoneDrag({ widthPx: measureTokenKeys(dragKeys), keys: dragKeys });
          }}
          onClick={(e) => {
            // Explorer semantics: plain replaces, Ctrl toggles, Shift ranges
            // through the anchor in ZONE order (designer/canvasSelection.ts).
            canvasSel.select(myKey, {
              additive: e.ctrlKey || e.metaKey,
              shift: e.shiftKey,
            });
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            canvasSel.select(myKey, { menu: true, owner: instanceId, x: e.clientX, y: e.clientY });
          }}
          onDoubleClick={(e) => {
            // Same shortcut the panel's ZoneChip offered: double-click a
            // literal-text token to edit it directly, no menu detour.
            if (!onRequestTextEdit || !isCustomTokenValue(rawValue)) return;
            e.preventDefault();
            e.stopPropagation();
            canvasSel.select(myKey);
            openTokenEdit(zoneKey, origIdx as number, rawValue);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              canvasSel.select(myKey);
              return;
            }
            // Keyboard reorder (DESIGN.md §7) — one slot at a time, hopping a
            // line break at a line's start/end (zoneMoveAt); a blocked nudge
            // pulses the token and whispers why (nudgeToken).
            if (
              edit &&
              e.ctrlKey &&
              !e.altKey &&
              !e.shiftKey &&
              (e.key === 'ArrowLeft' || e.key === 'ArrowRight') &&
              origIdx != null
            ) {
              e.preventDefault();
              e.stopPropagation();
              const dir = e.key === 'ArrowLeft' ? -1 : 1;
              const to = nudgeToken(zoneKey, rawArr, origIdx as number, dir, myKey);
              if (to === false) return;
              const nextKey = `token:${zoneKey}:${to}`;
              requestAnimationFrame(() => {
                (
                  (bandRef.current ?? document).querySelector(
                    `[data-token-key="${CSS.escape(nextKey)}"]`
                  ) as HTMLElement | null
                )?.focus();
              });
            }
          }}
        >
          {node}
          {/* Inline remove — visible on hover/selection, no keyboard or
              right-click detour needed (Josh, 2026-08-13). Last fragment
              only, so a wrapped literal offers exactly ONE ×. */}
          {edit &&
            (fragPos === 'only' || fragPos === 'last') &&
            ([-1, 1] as const).map((dir) => (
              <button
                key={dir}
                type="button"
                className={`dsg-token-nudge dsg-token-nudge-${dir < 0 ? 'l' : 'r'}`}
                title={dir < 0 ? 'Move left · Ctrl+←' : 'Move right · Ctrl+→'}
                aria-label={dir < 0 ? 'Move left · Ctrl+←' : 'Move right · Ctrl+→'}
                draggable={false}
                onMouseDown={(e) => e.stopPropagation()}
                onDragStart={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.stopPropagation();
                  nudgeToken(zoneKey, rawArr, origIdx as number, dir, myKey);
                }}
              >
                {dir < 0 ? (
                  <ChevronLeft size={10} strokeWidth={2.2} aria-hidden />
                ) : (
                  <ChevronRight size={10} strokeWidth={2.2} aria-hidden />
                )}
              </button>
            ))}
          {edit && (fragPos === 'only' || fragPos === 'last') && (
            <button
              type="button"
              className="dsg-token-x"
              title="Remove"
              aria-label={`Remove ${tokBase(t)}`}
              draggable={false}
              onMouseDown={(e) => e.stopPropagation()}
              onDragStart={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                kinHover(false);
                edit((f) => zoneRemoveAt(f, zoneKey, origIdx as number));
                canvasSel.select(null);
              }}
            >
              <X size={10} strokeWidth={2.2} aria-hidden />
            </button>
          )}
          {menuOpen && (
            <TokenFormatMenu
              value={rawValue}
              myKey={myKey}
              isCustom={isCustomTokenValue(rawValue)}
              sizeAware
              canEdit={!!edit}
              onEdit={
                onRequestTextEdit && isCustomTokenValue(rawValue)
                  ? () => openTokenEdit(zoneKey, origIdx as number, rawValue)
                  : undefined
              }
              onRemove={
                edit ? () => edit((f) => zoneRemoveAt(f, zoneKey, origIdx as number)) : undefined
              }
              onMove={
                edit
                  ? (dir) => nudgeToken(zoneKey, rawArr, origIdx as number, dir, myKey)
                  : undefined
              }
              onInsertLineBreak={
                onZoneInsertAt
                  ? () => onZoneInsertAt(zoneKey, (origIdx as number) + 1, '\n', null, null, false)
                  : undefined
              }
              onSetFixedDate={
                onRequestTextEdit && edit && tokBase(rawValue) === '{Date}'
                  ? () =>
                      onRequestTextEdit({
                        title: 'Use a fixed date',
                        initial: new Date().toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        }),
                        datePicker: true,
                        onCommit: (text) =>
                          edit((f) => zoneUpdateAt(f, zoneKey, origIdx as number, text)),
                      })
                  : undefined
              }
              anchorRef={{ current: spanRefs.current[myKey] ?? null }}
            />
          )}
        </span>
      ) : (
        node
      );
      nodes.push(
        <React.Fragment key={i}>
          {needSpace ? ' ' : ''}
          {wrapped}
        </React.Fragment>
      );
    });
    breaks.close.forEach((nl) => nodes.push(breakMarker(col, nl)));
    return nodes.length ? nodes : ' ';
  };

  // ─── The {Line} rule is a token too ───
  // It never reaches a row (the layout brain draws it as the band's one
  // rule), so without this it could be placed but never selected, moved or
  // removed on the paper. Same key, same selection, same × and right-click
  // as every other token.
  const lineAt = ((): { col: ZoneColumnKey; idx: number } | null => {
    for (const col of ['L', 'C', 'R'] as ZoneColumnKey[]) {
      const idx = rawArrOf(col).findIndex((t) => t.startsWith('{Line'));
      if (idx >= 0) return { col, idx };
    }
    return null;
  })();
  const ruleObj = (() => {
    if (!rule || !lineAt || !lineTok) return rule;
    const zoneKey = zoneKeyOf(lineAt.col);
    const key = `token:${zoneKey}:${lineAt.idx}`;
    const selected = canvasSel.selectedTokens.includes(key);
    const removeLine = () => {
      edit?.((f) => zoneRemoveAt(f, zoneKey, lineAt.idx));
      canvasSel.select(null);
    };
    return (
      <div
        data-token-key={key}
        role="button"
        tabIndex={0}
        draggable
        title="Line"
        aria-label="Line — click to select, drag to move, right-click for actions"
        aria-pressed={selected}
        className={`dsg-zone-ruleobj${selected ? ' is-selected' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          canvasSel.select(key, { additive: e.ctrlKey || e.metaKey, shift: e.shiftKey });
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            canvasSel.select(key);
          }
        }}
        onDragStart={(e) => {
          const copy = e.ctrlKey || e.metaKey;
          e.dataTransfer.setData(
            'application/json',
            JSON.stringify({
              type: 'token',
              value: lineTok,
              sourceZone: zoneKey,
              sourceIndex: lineAt.idx,
              copy,
            })
          );
          e.dataTransfer.effectAllowed = copy ? 'copy' : 'copyMove';
          beginZoneDrag({ widthPx: measureTokenKeys([key]), keys: [key] });
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          canvasSel.select(key);
          setSlotMenu({
            x: e.clientX,
            y: e.clientY,
            label: 'Line actions',
            items: [
              { label: 'Delete line', enabled: !!edit, danger: true, run: removeLine },
              ...(clip ? slotClipboardItems(clip, zoneKey, true) : []),
            ],
          });
        }}
      >
        {rule}
        {edit && (
          <button
            type="button"
            className="dsg-token-x"
            title="Remove line"
            aria-label="Remove line"
            draggable={false}
            onMouseDown={(e) => e.stopPropagation()}
            onDragStart={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation();
              removeLine();
            }}
          >
            <X size={10} strokeWidth={2.2} aria-hidden />
          </button>
        )}
      </div>
    );
  })();

  if (!rows.length && !rule) return null;

  // ─── The three column regions (Josh, 2026-08-13) ───
  // Clicking a column TARGETS it: the palette's click-to-add lands there, the
  // copy/cut/paste triad addresses it by `slot:<zoneKey>`, and right-click
  // gives it the same Paste / Copy all / Cut all menu the panel's DropZone
  // has. They sit UNDER the token spans (z-index 0 vs 1), so a click on a
  // token still selects the token; they carry no drag handlers, so ZoneShell's
  // own drop targets keep working by bubbling.
  const columnRegions = (
    <div className="dsg-zonecols">
      {(['L', 'C', 'R'] as ZoneColumnKey[]).map((c) => {
        const zk = zoneKeyOf(c);
        const targeted = canvasSel.selKey === `slot:${zk}`;
        return (
          <div
            key={c}
            className={`dsg-zonecol${targeted ? ' is-targeted' : ''}`}
            role="button"
            tabIndex={0}
            aria-pressed={targeted}
            aria-label={`${COLUMN_LABEL[c]} column of the ${kind} band — click to target it`}
            onClick={() => canvasSel.select(`slot:${zk}`)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                canvasSel.select(`slot:${zk}`);
              }
            }}
            onContextMenu={(e) => {
              if (!clip) return;
              e.preventDefault();
              e.stopPropagation();
              canvasSel.select(`slot:${zk}`);
              setSlotMenu({
                x: e.clientX,
                y: e.clientY,
                label: `${COLUMN_LABEL[c]} column actions`,
                items: slotClipboardItems(clip, zk, rawArrOf(c).length > 0),
              });
            }}
          />
        );
      })}
    </div>
  );

  return (
    <div
      ref={bandRef}
      className={`dsg-zone dsg-zone-${kind}${slot.dragging ? ' is-slot-dragging' : ''}`}
      aria-label={`${kind} band`}
      onDragOver={slot.handlers.onDragOver}
      onDragEnter={slot.handlers.onDragEnter}
      onDragLeave={slot.handlers.onDragLeave}
      onDrop={(e) => void slot.handlers.onDrop(e)}
    >
      {columnRegions}
      <SlotDropLayer
        ui={slot.ui}
        dragging={slot.dragging}
        columns={slotCols.current.map((c, i) => ({
          zoneKey: c.zoneKey,
          left: `${(i * 100) / 3}%`,
          width: `${100 / 3}%`,
          hasTokens: rawArrOf((['L', 'C', 'R'] as const)[i]).some(
            (t) => t !== '\n' && !t.startsWith('{Line')
          ),
        }))}
      />
      {!ruleBelow && ruleObj}
      {renderBandRows(rows, rowsSrc, rowSpacing, renderZone)}
      {ruleBelow && ruleObj}
      {/* A column holding ONLY returns has no content row to hang its ↵
          markers on — show them here so every blank line stays selectable
          and removable. */}
      {(['L', 'C', 'R'] as ZoneColumnKey[]).map((col) => {
        if (rowRawOf(col).some((r) => r.length > 0)) return null;
        const idxs: number[] = [];
        rawArrOf(col).forEach((v, i) => {
          if (v === '\n') idxs.push(i);
        });
        return idxs.length ? (
          <div key={`only-br-${col}`}>{idxs.map((i) => breakMarker(col, i))}</div>
        ) : null;
      })}
      {slotMenu && (
        <TokenSlotMenu
          at={{ x: slotMenu.x, y: slotMenu.y }}
          label={slotMenu.label}
          items={slotMenu.items}
          onClose={() => setSlotMenu(null)}
        />
      )}
    </div>
  );
}
